// Add a buyer to a seller deal — what "create a buyer engagement" means.
//
// A Buyer (buyside) engagement is one buyer's pursuit of one of our seller
// deals: the buyer's row on the deal's Interested buyers board (NDA,
// interest, BS call) plus the pursuit record DealCloud keeps per buyer per
// deal. The New engagement drawer used to create it bare — no deal, no
// roster row, nowhere to put the NDA (Gemspring on Diversified IS, Rosewood
// on Jack's Lawn Care). This drawer is the one way in, from the New
// engagement drawer's Buyer type and the board's "+ Add buyer"; the server
// side is POST /api/buyers/add-to-deal (app/buyer_add.py), the same code
// Ask Atlas and the MCP server's add_buyer_to_deal call.
//
// Documents (the signed NDA, redlines) ride on a note: the endpoint logs the
// note on the deal, tagged to the buyer, and the files upload onto it.

const BUYER_NDA_OPTIONS = [
  { value: 'not_sent', label: 'not sent', hue: 'slate' },
  { value: 'sent',     label: 'sent',     hue: 'amber' },
  { value: 'redlined', label: 'redlined', hue: 'amber', description: 'The buyer sent back markups' },
  { value: 'signed',   label: 'signed',   hue: 'green' },
  { value: 'declined', label: 'declined', hue: 'rose' },
];

// Seller deals only — a buyer pursuit can't be "bought into". Empty query →
// my team's live deals; typing searches every deal.
const searchSellerDealOptions = (q) => window.AtlasApi.listEngagements({
  ...(q ? { q } : { my_team: true, active_only: true }),
  exclude_engagement_type: 'buyer_buyside', limit: 25,
}).then((r) => (r.items || []).map((e) => ({
  value: e.id,
  label: e.name,
  hint: [e.company_name && e.company_name !== e.name ? e.company_name : null, e.stage_name].filter(Boolean).join(' · ') || undefined,
})));

const _todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const _sectionLabel = { fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 };

// `deal` ({id, name}) locks the deal (the board's button); `initialDeal`
// pre-picks it but leaves it changeable (the page you're on, via "+ New");
// `initialBuyer` ({id, name}) pre-picks the firm. `typeField` is the New
// engagement drawer's Type select, rendered first so switching back stays
// one click.
// `toastAction` ({label, onClick}) rides on the success toast (the Market workspace's Open Buyers).
const AddBuyerToDealDrawer = ({
  deal, initialDeal, initialBuyer, onClose, onDone, typeField, toastAction,
  title = 'Add a buyer',
  subtitle,
  submitLabel = 'Add buyer',
}) => {
  // Phone: the Drawer is a full-screen sheet — the picker + New company pair
  // and the NDA pair stack, and the footer buttons split the width.
  const phone = !!window.useDevice().phone;
  const startDeal = deal || initialDeal || null;
  const [dealId, setDealId] = React.useState(startDeal?.id || '');
  const [dealLabel, setDealLabel] = React.useState(startDeal?.name || '');
  const [buyerId, setBuyerId] = React.useState(initialBuyer?.id || '');
  const [buyerLabel, setBuyerLabel] = React.useState(initialBuyer?.name || '');
  // Static picker extras (preset firm, just-created firm); search covers the rest.
  const [buyerOpts, setBuyerOpts] = React.useState(initialBuyer?.id ? [{ value: initialBuyer.id, label: initialBuyer.name }] : []);
  // null = untouched: a new buyer opens at Engagement Created / NDA not
  // sent, and a buyer already on the deal keeps what it has.
  const [stage, setStage] = React.useState(null);
  const [nda, setNda] = React.useState(null);
  const [ndaDate, setNdaDate] = React.useState('');
  const [note, setNote] = React.useState('');
  const [files, setFiles] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [creatingCompany, setCreatingCompany] = React.useState(false);
  const [industries, setIndustries] = React.useState(null);
  const [members, setMembers] = React.useState([]);
  // The deal's board, to say "already on this deal" before saving.
  const [board, setBoard] = React.useState(null);

  React.useEffect(() => {
    if (!dealId) { setBoard(null); return undefined; }
    let alive = true;
    window.AtlasApi.buyersForEngagement(dealId)
      .then((r) => { if (alive) setBoard((r && r.items) || []); })
      .catch(() => { if (alive) setBoard([]); });
    return () => { alive = false; };
  }, [dealId]);
  const existing = (board || []).find((b) => b.buyer_company_id === buyerId) || null;
  const stageShown = stage || (existing ? (window.pursuitKeyFor ? window.pursuitKeyFor(existing.pursuit_stage) : null) : 'created') || '';
  const ndaShown = nda || (existing && existing.nda_status) || 'not_sent';
  // The date the buyer's row already carries, until one is typed or picked.
  const ndaDateShown = ndaDate || (existing && existing.nda_signed_date) || '';

  const pickNda = (v) => {
    setNda(v);
    if (v === 'signed' && !ndaDate) setNdaDate((existing && existing.nda_signed_date) || _todayIso());
  };

  const openCompanyCreate = () => {
    if (industries === null) {
      window.AtlasApi.listIndustries()
        .then((i) => setIndustries(Array.isArray(i) ? i : []))
        .catch(() => setIndustries([]));
      window.AtlasData.loadTeamMembers()
        .then((m) => setMembers(Array.isArray(m) ? m : []))
        .catch(() => setMembers([]));
    }
    setCreatingCompany(true);
  };

  const canSubmit = dealId && buyerId && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    const firm = buyerLabel || 'Buyer';
    const ndaLabel = (BUYER_NDA_OPTIONS.find((o) => o.value === ndaShown) || {}).label;
    let body = note.trim();
    if (!body && files.length) {
      body = `${files.length === 1 ? 'Document' : 'Documents'} for ${firm}: ${files.map((f) => f.name).join(', ')}`;
    }
    const payload = {
      engagement_id: dealId,
      buyer_company_id: buyerId,
      ...(stage ? { pursuit_stage: stage } : {}),
      ...(nda ? { nda_status: nda } : {}),
      ...(ndaShown === 'signed' && ndaDate ? { nda_signed_date: ndaDate } : {}),
      ...(body ? {
        note: body,
        note_title: ndaShown !== 'not_sent' ? `NDA ${ndaLabel} — ${firm}` : `${firm} — ${dealLabel || 'buyer'}`,
      } : {}),
    };
    try {
      const out = await window.AtlasApi.addBuyerToDeal(payload);
      if (files.length && out.note) await window.uploadPendingAttachments(out.note, files);
      pushToast({
        type: 'success',
        title: out.added ? `${out.firm} added to ${out.deal}` : `${out.firm} updated on ${out.deal}`,
        body: out.pursuit_created ? 'Buyer engagement opened.' : undefined,
        action: toastAction,
      });
      if (out.pursuit_note) pushToast({ type: 'warning', title: 'No buyer engagement yet', body: out.pursuit_note });
      window.dispatchEvent(new CustomEvent('atlas-data-changed'));
      onDone && onDone(out);
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not add the buyer', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width={540}
      widthKey="addBuyer"
      footer={phone ? (
        <div style={{ flex: 1, display: 'flex', gap: 10 }}>
          <Btn variant="outline" onClick={onClose} disabled={busy} style={{ flex: 1, justifyContent: 'center' }}>Cancel</Btn>
          <Btn variant="accent" onClick={submit} disabled={!canSubmit} style={{ flex: 1, justifyContent: 'center' }}>
            {busy ? 'Saving…' : (existing ? 'Update buyer' : submitLabel)}
          </Btn>
        </div>
      ) : (
        <>
          <Btn size="sm" variant="outline" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={submit} disabled={!canSubmit}>
            {busy ? 'Saving…' : (existing ? 'Update buyer' : submitLabel)}
          </Btn>
        </>
      )}
    >
      {typeField}
      <Field label="Deal they're buying into" hint={deal ? undefined : 'The seller deal — search by deal or company name.'}>
        <Combobox value={dealId} disabled={!!deal}
          onChange={(e) => { setDealId(e.target.value); setDealLabel(e.label || ''); }}
          placeholder="Search seller deals…"
          options={startDeal ? [{ value: startDeal.id, label: startDeal.name }] : []}
          loadOptions={deal ? undefined : searchSellerDealOptions} />
      </Field>
      <Field label="Buyer firm" hint="The PE firm or strategic buying in — search, or create it here.">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', ...(phone ? { flexDirection: 'column', alignItems: 'stretch', gap: 8 } : null) }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Combobox value={buyerId}
              onChange={(e) => {
                setBuyerId(e.target.value);
                setBuyerLabel(e.label || (buyerOpts.find((o) => o.value === e.target.value) || {}).label || '');
                setStage(null); setNda(null); setNdaDate('');
              }}
              placeholder="Search companies…"
              options={buyerOpts}
              loadOptions={window.searchCompanyOptions} />
          </div>
          <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />}
            onClick={openCompanyCreate} title="Create a buyer firm that isn't in Atlas yet"
            style={phone ? { alignSelf: 'flex-start' } : undefined}>
            New company
          </Btn>
        </div>
      </Field>
      {existing && (
        <div style={{ margin: '-4px 0 12px', padding: '9px 11px', borderRadius: 6, fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)',
                      border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          <b style={{ color: 'var(--text)' }}>{buyerLabel || 'This buyer'}</b> is already on {dealLabel || 'this deal'}
          {' '}({[existing.pursuit_stage ? window.cleanDcStage ? window.cleanDcStage(existing.pursuit_stage) : existing.pursuit_stage : 'no buyer engagement yet',
                 `NDA ${(existing.nda_status || 'not_sent').replace('_', ' ')}`].join(' · ')}). Saving updates it.
        </div>
      )}
      <Field label="Pursuit stage" hint="Where the buyer is in our process — Lost means they passed.">
        <Select value={stageShown} onChange={(e) => setStage(e.target.value || null)}
          options={window.PURSUIT_STAGE_OPTIONS || []} />
      </Field>
      <div style={{ display: 'flex', gap: 8, ...(phone ? { flexDirection: 'column', gap: 0 } : null) }}>
        <div style={{ flex: 1 }}>
          <Field label="NDA">
            <Select value={ndaShown} onChange={(e) => pickNda(e.target.value)} options={BUYER_NDA_OPTIONS} />
          </Field>
        </div>
        <div style={{ flex: 1 }}>
          {ndaShown === 'signed' && (
            <Field label="NDA signed on">
              <TextInput type="date" value={ndaDateShown} onChange={(e) => { setNdaDate(e.target.value); if (!nda) setNda('signed'); }} />
            </Field>
          )}
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={_sectionLabel}>Documents</div>
        <window.ComposerAttachments files={files} onChange={setFiles} disabled={busy} />
        <div style={{ fontSize: phone ? 13 : 10.5, color: 'var(--text-4)', marginTop: 3 }}>
          Signed NDA, redlines… Saved on a note on the deal, tagged to the buyer.
        </div>
      </div>
      <Field label="Note">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
          placeholder="Optional — what happened (e.g. sent back redlines on the non-solicit)"
          style={{ width: '100%', padding: '6px 8px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 5,
                   background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', resize: 'vertical' }} />
      </Field>
      {/* Portaled to body so the stacked drawer isn't nested inside this
          drawer's <Field> label (label click-forwarding + focus traps). */}
      {creatingCompany && ReactDOM.createPortal(
        <window.NewCompanyDrawer
          industries={industries || []}
          members={members}
          onClose={() => setCreatingCompany(false)}
          onCreated={(company) => {
            setCreatingCompany(false);
            if (company?.id) {
              setBuyerOpts((prev) => [{ value: company.id, label: company.name }, ...prev]);
              setBuyerId(company.id);
              setBuyerLabel(company.name);
            }
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />, document.body)}
    </Drawer>
  );
};

window.AddBuyerToDealDrawer = AddBuyerToDealDrawer;
window.BUYER_NDA_OPTIONS = BUYER_NDA_OPTIONS;
